import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Tooltip,
  Container,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HelpIcon from '@mui/icons-material/Help';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import TLS_Logo from '../images/trueleasing_logo.png';

// Navigation items configuration
const NAVIGATION_ITEMS = [
  { title: 'Admin', path: '/thewonderblueadmin_admin' },
  // { title: 'Booking', path: '/' },
  { title: 'Check-in', path: '/thewonderblueadmin_checkin' },
  { title: 'Manage boat rounds', path: '/thewonderblueadmin_BoatPlaner' },
  { title: 'Manage boat', path: '/thewonderblueadmin_boatmangement' },
  { title: 'User Account', path: '/thewonderblueadmin_employee' },
  { title: 'Lounge', path: '/thewonderblueadmin_room' },
  // { title: 'Coupon', path: '/thewonderblueadmin_coupon' }
];

// Component for the navigation buttons
const NavigationButton = ({ title, path }) => (
  <Button
    component={Link}
    to={path}
    sx={{
      my: 2,
      color: 'black',
      display: 'block',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        borderBottom: '2px solid #1976d2'
      }
    }}
  >
    {title}
  </Button>
);

// Component for the user profile section in menu
const UserProfileSection = ({ fullName, onClose }) => (
  <MenuItem onClick={onClose}>
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      alignItems: 'center',
      p: 1,
      boxShadow: 1
    }}>
      <Avatar sx={{ width: 60, height: 60, mb: 1 }} alt="User Avatar" src="/static/images/avatar/2.jpg" />
      <Typography variant="h6">{fullName}</Typography>
      <Divider />
      <Typography variant="body2" color="text.secondary">ดูโปรไฟล์ทั้งหมด</Typography>
    </Box>
  </MenuItem>
);

// Component for menu items in user menu
const UserMenuItem = ({ icon: Icon, text, onClick }) => (
  <MenuItem onClick={onClick}>
    <ListItemIcon>
      <Icon sx={{ color: 'black' }} />
    </ListItemIcon>
    <ListItemText primary={text} />
  </MenuItem>
);

// Dynamic Avatar Component
const UserAvatar = ({ fullName }) => {
  const initials = fullName
    .split(' ')
    .map((word) => word[0])
    .join('');
  return (
    <Avatar
      alt="User Avatar"
      src="/static/images/avatar/2.jpg"
      sx={{ backgroundColor: '#1976d2', color: 'white' }}
    >
      {initials}
    </Avatar>
  );
};

const ResponsiveAppBar = () => {
  const [navMenu, setNavMenu] = useState(null);
  const [userMenu, setUserMenu] = useState(null);
  const [fullName, setFullName] = useState('ชื่อจริง นามสกุล');
  const navigate = useNavigate();

  const NavigationMenuItem = ({ title, path, onClose }) => (
    <MenuItem
      onClick={() => {
        onClose();
        navigate(path);
      }}
    >
      <Typography textAlign="center">{title}</Typography>
    </MenuItem>
  );

  useEffect(() => {
    const firstName = Cookies.get('first_name');
    const lastName = Cookies.get('last_name');
    if (firstName && lastName) {
      setFullName(`${firstName} ${lastName}`);
    }
  }, []);

  const handleLogout = () => {
    ['token', 'first_name', 'last_name'].forEach((cookie) => Cookies.remove(cookie));
    navigate('/login');
  };

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: 'white',
        color: 'black',
        boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2)',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Desktop Logo */}
          <Box sx={{ mr: 2, display: { xs: 'none', md: 'flex' } }}>
            <img
              src={"https://s3.ap-southeast-1.amazonaws.com/boatrental.trueleasing.co.th/shareticket/trueleasing_logo.png"}
              alt="TrueLeasing Logo"
              style={{ height: '50px', marginRight: '30px' }}
            />
          </Box>

          {/* Mobile Navigation */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="Open navigation menu"
              onClick={(e) => setNavMenu(e.currentTarget)}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={navMenu}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(navMenu)}
              onClose={() => setNavMenu(null)}
              sx={{ display: { xs: 'block', md: 'none' } }}
            >
              {NAVIGATION_ITEMS.map((item) => (
                <NavigationMenuItem
                  key={item.title}
                  {...item}
                  onClose={() => setNavMenu(null)}
                />
              ))}
            </Menu>
          </Box>

          {/* Mobile Logo */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <img
              src={"https://s3.ap-southeast-1.amazonaws.com/boatrental.trueleasing.co.th/shaceticket/trueleasing_logo.png"}
              alt="TrueLeasing Logo"
              style={{ height: '50px', marginRight: '30px' }}
            />
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {NAVIGATION_ITEMS.map((item) => (
              <NavigationButton key={item.title} {...item} />
            ))}
          </Box>

          {/* User Menu */}
          <Box sx={{ flexGrow: 0 }}>
            <Tooltip title="Open settings">
              <IconButton
                onClick={(e) => setUserMenu(e.currentTarget)}
                sx={{ p: 0 }}
                aria-label="Open user menu"
              >
                <UserAvatar fullName={fullName} />
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: '45px' }}
              anchorEl={userMenu}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(userMenu)}
              onClose={() => setUserMenu(null)}
              PaperProps={{
                style: {
                  width: '320px',
                  backgroundColor: 'white',
                  color: 'black',
                },
              }}
            >
              <UserProfileSection
                fullName={fullName}
                onClose={() => setUserMenu(null)}
              />
              <UserMenuItem
                icon={HelpIcon}
                text="ความช่วยเหลือและการสนับสนุน"
                onClick={() => setUserMenu(null)}
              />
              <UserMenuItem
                icon={ExitToAppIcon}
                text="ออกจากระบบ"
                onClick={handleLogout}
              />
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default ResponsiveAppBar;